import Button from "components/library/Button";
import {
  ActiveLearningLogo,
  AnnotationTeamsLogo,
  ProgrammaticLogo,
} from "components/ProductsLogos";
import { SectionBadge } from "components/SectionBadge";
import { ArrowRightIcon } from "@heroicons/react/outline";
import { AnnotateIcon } from "../library/Icons";
import { CTASmall } from "./CTASmall";
import { FeatureCard } from "./FeatureCard";
import { Quote } from "./Quote";
import { SectionTitleAndDescription } from "./SectionTitleAndDescription";
import { Lissajous1 } from "@components/Lissajous";
import { useBreakpoints } from "hooks/useBreakpoints";

export const AnnotateSection = () => {
  const { isMediumWidth } = useBreakpoints();

  return (
    <div className="bg-gray-100 dark:bg-black">
      <section className="container-xl">
        <div className=" relative py-48 md:pt-160">
          <div className="mx-12 mb-64 md:mx-[64px]">
            <div className="mb-12 -ml-4 md:mb-20 ">
              <SectionBadge>
                <Lissajous1 />
                Annotate
              </SectionBadge>
            </div>
            <SectionTitleAndDescription
              title="Getting labeled data has never been this easy"
              description={
                <>
                  <p>Your models are only as good as your data.</p>
                  <br />

                  <p>
                    Our intelligent tools get you annotated data faster than any
                    alternative on the market.
                  </p>
                </>
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-16">
            <FeatureCard
              wide
              title={<ProgrammaticLogo size={isMediumWidth ? 24 : 16} />}
              description={"The powerful annotation tool for weak supervision."}
              callouts={[
                {
                  title: "No hand labeling required",
                  description:
                    "Write labeling functions instead of manually labeling data",
                },
                {
                  title: "Instant feedback",
                  description:
                    "See the impact and explore your data in real time",
                },
                {
                  title: "No-code rule builders",
                  description:
                    "Intuitive UI to rapidly convert ideas into labels",
                },
                {
                  title: "Works with the tools you love",
                  description: "Integrates with your existing ML pipeline.",
                },
              ]}
              learnMore={
                <Button
                  href={"/programmatic"}
                  styling="ghost"
                  className="-ml-12"
                  IconRight={ArrowRightIcon}
                >
                  Learn more
                </Button>
              }
              image={
                <div className="mx-16 flex justify-center">
                  <img
                    className=" -mb-[200px] w-full rounded-ms border border-gray-400 shadow-c dark:border-none dark:shadow-none md:mt-20 lg:w-3/5"
                    src="/assets/product/programmatic-vector-run.svg"
                    alt="Programmatic"
                  />
                </div>
              }
            />
            <FeatureCard
              title={<AnnotationTeamsLogo size={isMediumWidth ? 24 : 16} />}
              description={
                "Best-in-class hosted annotation platform with team management features."
              }
              callouts={[
                {
                  title: "Collaborate with annotators",
                  description:
                    "Annotation tooling to get the best from your subject-matter experts",
                },
              ]}
              image={
                <img
                  className="mx-auto max-h-[240px] w-4/5  rounded-ms text-left md:mx-0 md:h-[180px]"
                  src="/assets/product/annotation-team.svg"
                  alt="Team members"
                />
              }
            />
            <FeatureCard
              title={<ActiveLearningLogo size={isMediumWidth ? 24 : 16} />}
              description={
                "Speed up your labeling 10x by focusing on the challenging data for your model."
              }
              callouts={[
                {
                  title: "10x fewer annotations needed",
                  description:
                    "Model assisted labeling to maximize performance",
                },
              ]}
              image={
                <img
                  className="mx-auto max-h-[200px] w-4/5 rounded-ms md:mx-0 md:h-[173px] "
                  src="/assets/product/active-learning-interface.svg"
                  alt="Team members"
                />
              }
            />
          </div>
          <div className="my-[104px] grid grid-cols-1 justify-center gap-y-48 md:grid-cols-2">
            <CTASmall text="Annotate data and train a model in minutes" />
            <div className="md:px-16">
              <Quote
                quote="Programmatic is the way to go for NLP projects. This is literally how I imagine humans working with AI, actively interacting with data and having instant feedback on how to rapidly iterate with models."
                // quote="I'm really liking how Humanloop solves NLP for Pluggy. It makes our focus to be on our product, using our teams to annotate, without having to care about pains that the AI industry has."
                // "I've been using the new Weak Labelling tool made by @HumanloopML for a Named Entity Recognition project. It is phenomenal. It basically allows you to bootstrap massive NLP datasets to train machine learning models on, with just a few lines of code/regex.
                name="Henry Maguire"
                company="Data Scientist at Orbital Witness"
                image="/assets/customers/henry_maguire.jpeg"
                products={[ProgrammaticLogo, ActiveLearningLogo]}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
